'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import {
    ToggleButton as AriaToggleButton,
    type ToggleButtonProps as AriaToggleButtonProps,
    type ToggleButtonRenderProps
} from 'react-aria-components/ToggleButton';
import { cn } from '../../utils/cn';
import { baseButtonClasses, buttonVariants, createButtonInteractionHandlers } from '../Button/Button.tsx';

const toggleButtonBase = cva(baseButtonClasses, {
    variants: {
        size: {
            xs: 'size-lr-size-400 p-lr-spacing-050',
            sm: 'size-lr-size-500 p-lr-spacing-100',
            md: 'size-lr-size-600 p-lr-spacing-150'
        },
        isSelected: {
            false: `
                bg-lr-bg-button-toggle-inactive-default text-lr-icon-main
                data-disabled:bg-lr-bg-button-toggle-inactive-disabled data-disabled:text-lr-icon-disabled
                data-hovered:bg-lr-bg-button-toggle-inactive-hover
                data-pressed:bg-lr-bg-button-toggle-inactive-pressed
            `,
            true: `
                bg-lr-bg-button-toggle-active-default text-lr-icon-main-inverse
                data-disabled:bg-lr-bg-button-toggle-active-disabled data-disabled:text-lr-icon-disabled-inverse
                data-hovered:bg-lr-bg-button-toggle-active-hover
                data-pressed:bg-lr-bg-button-toggle-active-pressed
            `
        }
    },
    defaultVariants: {
        size: 'md',
        isSelected: false
    }
});

type ToggleButtonVariantProps = VariantProps<typeof toggleButtonBase>;

type ToggleButtonChildren = React.ReactNode | ((props: ToggleButtonRenderProps) => React.ReactNode);

export interface ToggleButtonProps
    extends Omit<AriaToggleButtonProps, 'className' | 'children'>,
        Omit<ToggleButtonVariantProps, 'isSelected'> {
    className?: string;
    children?: ToggleButtonChildren;
    ref?: React.Ref<HTMLButtonElement> | React.RefObject<HTMLButtonElement>;
    pill?: boolean;
    onBackground?: boolean;
}

function ToggleButton({
    className,
    size,
    ref,
    children,
    onClick,
    pill,
    onBackground,
    ...rest
}: ToggleButtonProps) {
    const { handleClick, handleKeyDown } = createButtonInteractionHandlers({
        isDisabled: rest?.isDisabled,
        onClick: onClick
    });

    const getClassName = (isSelected: boolean) => {
        if (pill) {
            return cn(
                buttonVariants({
                    size,
                    pill: true,
                    onBackground,
                    variant: isSelected ? 'primary' : 'secondary'
                }),
                isSelected ? 'text-lr-subtitle-tight-medium' : 'text-lr-subtitle-tight-light',
                !isSelected &&
                    `
                        bg-transparent shadow-none
                        data-hovered:bg-lr-bg-button-toggle-inactive-hover data-hovered:shadow-none
                    `,
                className
            );
        }
        return cn(toggleButtonBase({ size, isSelected }), className);
    };

    return (
        <AriaToggleButton
            {...rest}
            ref={ref}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={({ isSelected }) => getClassName(isSelected)}
        >
            {children}
        </AriaToggleButton>
    );
}

ToggleButton.displayName = 'TwToggleButton';

export { ToggleButton };
export default ToggleButton;
